import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslate } from 'src/locales';
import LoadingScreen from 'src/components/shared-components/loading-screen';
import SelectedServicesPanel from 'src/components/shared-components/selected-services-panel';
import ByzMuiAddButton from 'src/components/core-components/mui/buttons/ByzMuiAddButton';
import { IsolationOption, IsolationRow } from '../models';
import ByzEditButton from 'src/components/core-components/mui/buttons-grid/ByzGrdEditButton';

type IsolationSelectionModalProps = {
  open: boolean;
  onClose: () => void;
  isIsolationTypeLoading: boolean;
  isPatientIsolationsLoading: boolean;
  isSavingPendingIsolations: boolean;
  isolationOptions: IsolationOption[];
  addedIsolations: IsolationRow[];
  pendingIsolations: IsolationRow[];
  setPendingIsolations: React.Dispatch<React.SetStateAction<IsolationRow[]>>;
  getImageSrc: (base64: string | null | undefined) => string;
  handleCommitPendingIsolations: () => void;
  onEditPendingIsolation?: (row: IsolationRow, index: number) => void;
};

const IsolationSelectionModal: React.FC<IsolationSelectionModalProps> = ({
  open,
  onClose,
  isIsolationTypeLoading,
  isPatientIsolationsLoading,
  isSavingPendingIsolations,
  isolationOptions,
  addedIsolations,
  pendingIsolations,
  setPendingIsolations,
  getImageSrc,
  handleCommitPendingIsolations,
  onEditPendingIsolation,
}) => {
  const { t } = useTranslate();

  const [searchTerm, setSearchTerm] = useState('');
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [densityMenuAnchorEl, setDensityMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [rowDensity, setRowDensity] = useState<'compact' | 'standard' | 'comfortable'>('standard');
  const [columnVisibility, setColumnVisibility] = useState({
    icon: true,
    name: true,
    description: true,
    handHygiene: true,
    gloves: true,
    gown: true,
    mask: true,
    eyeProtection: true,
    patientTransfer: true,
  });
  const [isModalSelectionsExpanded, setIsModalSelectionsExpanded] = useState(true);
  const [isEyeOpen, setIsEyeOpen] = useState(true);

  const filteredOptions = useMemo(
    () =>
      isolationOptions.filter(
        (option) =>
          option.is_active === '1' &&
          (option.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            option.descrption?.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    [isolationOptions, searchTerm]
  );

  const selectable = filteredOptions.filter(
    (option) => !addedIsolations.some((item) => item.ist_id === option.id)
  );

  const allSelectableChecked =
    selectable.length > 0 &&
    selectable.every((option) => pendingIsolations.some((p) => p.ist_id === option.id));

  const rowHeight =
    rowDensity === 'compact' ? 32 : rowDensity === 'comfortable' ? 56 : 40;

  const handleToggleColumnVisibility = (key: keyof typeof columnVisibility) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChangeDensity = (value: 'compact' | 'standard' | 'comfortable') => {
    setRowDensity(value);
    setDensityMenuAnchorEl(null);
  };

  const handleExportCsv = () => {
    const headers = ['Adı', 'Açıklama', 'El Hijyeni', 'Eldiven', 'Önlük', 'Maske', 'Gözlük', 'Hastanın Transferi'];
    const rows = filteredOptions.map((option) => [
      option.name,
      option.descrption,
      option.hand_hygiene ?? option.elHijyeni,
      option.glove ?? option.eldiven,
      option.bib ?? option.onluk,
      option.mask ?? option.maske,
      option.glasses ?? option.gozluk,
      option.transfer_of_patient ?? option.hastaninTransferi,
    ]);
    const csvContent = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'izolasyon_listesi.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderSelectionsTable = (rowsOverride?: IsolationRow[], maxHeight?: string | number) => {
    const rows = rowsOverride ?? pendingIsolations;
    const isPendingTable = rowsOverride === pendingIsolations;
    const resolvedMaxHeight = maxHeight ?? '500px';

    if (isPatientIsolationsLoading) {
      return (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ boxShadow: '0 2px 8px 0 rgba(60,72,88,0.04)', maxHeight: resolvedMaxHeight, overflow: 'auto' }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <LoadingScreen />
          </Box>
        </TableContainer>
      );
    }

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ boxShadow: '0 2px 8px 0 rgba(60,72,88,0.04)', maxHeight: resolvedMaxHeight, overflow: 'auto' }}
      >
        <Table sx={{ borderCollapse: 'collapse' }}>
          <TableHead
            style={{ display: isEyeOpen ? 'table-header-group' : 'none' }}
            sx={{
              '& th': {
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                fontSize: '12px',
                lineHeight: '24px',
                letterSpacing: '0.17px',
                backgroundColor: '#F4F7FE',
                color: '#000000',
                border: '1px solid #E0E0E0',
                textAlign: 'center',
              },
            }}
          >
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  py: 1,
                  px: 1,
                  width: 40,
                }}
              />
              <TableCell colSpan={2} sx={{ py: 1, px: 2, width: '150px' }}>
                {t('isolation-requests.grid-columns.isolation')}
              </TableCell>
              <TableCell colSpan={2} sx={{ py: 1, px: 2 }}>
                {t('isolation-requests.form.isolation-take-reason')}
              </TableCell>
              <TableCell colSpan={2} sx={{ py: 1, px: 2 }}>
                {t('isolation-requests.form.non-bacterial-isolation-reason')}
              </TableCell>
              <TableCell colSpan={3} sx={{ py: 1, px: 2 }}>
                {t('isolation-requests.form.isolation-notification')}
              </TableCell>
              <TableCell colSpan={5} sx={{ py: 1, px: 2 }}>
                {t('isolation-requests.form.isolation-entry')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                padding="checkbox"
              >
                {isPendingTable ? (
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={rows.length > 0}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setPendingIsolations([]);
                      }
                    }}
                  />
                ) : (
                  <Checkbox color="primary" size="small" checked={false} disabled />
                )}
              </TableCell>
              <TableCell>
                {t('isolation-requests.isolation-selection.columns.icon')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.isolation-selection.columns.isolation-name')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.form.isolation-reason')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.form.description')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.form.isolation-reason')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.form.description')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.form.microbiology')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.form.kitchen-team')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.form.cleaning-team')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.start-date')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.end-date')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.form.patient-and-family')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.status')}
              </TableCell>
              <TableCell>
                {t('isolation-requests.edit')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((option, idx) => {
              const isPendingTableRow = isPendingTable;
              return (
                <TableRow
                  key={`selected-${option.id}`}
                  sx={{ height: 70, '& > td': { verticalAlign: 'middle', textAlign: 'center' } }}
                >
                  <TableCell padding="checkbox" sx={{ py: 0.5, px: 1, textAlign: 'center' }}>
                    {isPendingTableRow ? (
                      <Checkbox
                        color="primary"
                        size="small"
                        checked
                        onChange={() => {
                          setPendingIsolations((prev) =>
                            prev.filter((item) => item.id !== option.id)
                          );
                        }}
                      />
                    ) : (
                      <Checkbox color="primary" size="small" checked disabled />
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {option.icon && (
                      <img
                        src={getImageSrc(option.icon)}
                        alt={option.name}
                        style={{ width: 32, height: 32, display: 'block', margin: '0 auto' }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>{option.isolationtypename}</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>{option.isolation_taken_reason_label || '-'}</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>{option.isolation_taken_descrption || '-'}</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>{option.isolation_without_bacteria_label || '-'}</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>{option.isolation_without_bact_descrp || '-'}</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {option.notification_lab === '1'
                      ? t('isolation-requests.common-yes') || 'Evet'
                      : t('isolation-requests.common-no') || '-'}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {option.notification_kitchen === '1'
                      ? t('isolation-requests.common-yes') || 'Evet'
                      : t('isolation-requests.common-no') || '-'}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {option.notification_cleaner === '1'
                      ? t('isolation-requests.common-yes') || 'Evet'
                      : t('isolation-requests.common-no') || '-'}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {option.start_date ? new Date(option.start_date).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {option.finish_date ? new Date(option.finish_date).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {option.is_given_family_education === '1'
                      ? t('isolation-requests.common-yes') || 'Evet'
                      : t('isolation-requests.common-no') || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 0.5,
                      px: 1,
                      backgroundColor: (() => {
                        const status = isPendingTableRow || !option.approve_status ? '0' : option.approve_status;
                        return status === '0'
                          ? '#FFCC004D'
                          : status === '1'
                            ? '#AEADA64D'
                            : status === '2'
                              ? '#34C7594D'
                              : 'transparent';
                      })(),
                      fontWeight: 700,
                    }}
                  >
                    {(() => {
                      const status = isPendingTableRow || !option.approve_status ? '0' : option.approve_status;
                      return status === '0'
                        ? t('isolation-requests.status-labels.pending')
                        : status === '1'
                          ? t('isolation-requests.status-labels.sent')
                          : status === '2'
                            ? t('isolation-requests.status-labels.approved')
                            : status === '3'
                              ? t('isolation-requests.status-labels.cancelled')
                              : status === '4'
                                ? t('isolation-requests.status-labels.completed')
                                : '';
                    })()}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {isPendingTableRow && onEditPendingIsolation && (
                      <ByzEditButton
                        size="small"
                        onClick={() => onEditPendingIsolation(option, idx)}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>{t('isolation-requests.isolation-selection.title')}</DialogTitle>
      <DialogContent
        dividers
        sx={{ height: 600, display: 'flex', flexDirection: 'column', position: 'relative' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
            px: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="text"
              size="small"
              color="primary"
              startIcon={<ViewColumnIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1 }}
              onClick={(e) => setColumnsMenuAnchorEl(e.currentTarget)}
            >
              Sütunlar
            </Button>
            <Button
              variant="text"
              size="small"
              color="primary"
              startIcon={<FilterListIcon fontSize="small" sx={{ color: '#00B8D9' }} />}
              sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1 }}
            >
              FİLTRELE
            </Button>
            <Button
              variant="text"
              size="small"
              color="primary"
              startIcon={<DensityMediumIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1 }}
              onClick={(e) => setDensityMenuAnchorEl(e.currentTarget)}
            >
              Satır Aralığı
            </Button>
            <Button
              variant="text"
              size="small"
              color="primary"
              startIcon={<DownloadIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1 }}
              onClick={handleExportCsv}
            >
              Dışa Aktar
            </Button>
          </Box>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <TextField
              placeholder={'Ara'}
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#00B8D9', mr: 1 }} fontSize="small" />,
              }}
              sx={{ width: 220 }}
            />
          </Box>
        </Box>

        {/* Sütunlar menüsü */}
        <Menu anchorEl={columnsMenuAnchorEl} open={Boolean(columnsMenuAnchorEl)} onClose={() => setColumnsMenuAnchorEl(null)}>
          <MenuItem dense onClick={() => handleToggleColumnVisibility('icon')}>
            <Checkbox size="small" checked={columnVisibility.icon} sx={{ mr: 1 }} />
            {t('isolation-requests.isolation-selection.columns.icon')}
          </MenuItem>
          <MenuItem dense onClick={() => handleToggleColumnVisibility('name')}>
            <Checkbox size="small" checked={columnVisibility.name} sx={{ mr: 1 }} />
            {t('isolation-requests.isolation-selection.columns.isolation-name')}
          </MenuItem>
          <MenuItem dense onClick={() => handleToggleColumnVisibility('description')}>
            <Checkbox size="small" checked={columnVisibility.description} sx={{ mr: 1 }} />
            {t('isolation-requests.isolation-selection.columns.description')}
          </MenuItem>
          <MenuItem dense onClick={() => handleToggleColumnVisibility('handHygiene')}>
            <Checkbox size="small" checked={columnVisibility.handHygiene} sx={{ mr: 1 }} />
            {t('isolation-requests.isolation-selection.columns.hand-hygiene')}
          </MenuItem>
          <MenuItem dense onClick={() => handleToggleColumnVisibility('gloves')}>
            <Checkbox size="small" checked={columnVisibility.gloves} sx={{ mr: 1 }} />
            {t('isolation-requests.isolation-selection.columns.gloves')}
          </MenuItem>
          <MenuItem dense onClick={() => handleToggleColumnVisibility('gown')}>
            <Checkbox size="small" checked={columnVisibility.gown} sx={{ mr: 1 }} />
            {t('isolation-requests.isolation-selection.columns.gown')}
          </MenuItem>
          <MenuItem dense onClick={() => handleToggleColumnVisibility('mask')}>
            <Checkbox size="small" checked={columnVisibility.mask} sx={{ mr: 1 }} />
            {t('isolation-requests.isolation-selection.columns.mask')}
          </MenuItem>
          <MenuItem dense onClick={() => handleToggleColumnVisibility('eyeProtection')}>
            <Checkbox size="small" checked={columnVisibility.eyeProtection} sx={{ mr: 1 }} />
            {t('isolation-requests.isolation-selection.columns.eye-protection')}
          </MenuItem>
          <MenuItem dense onClick={() => handleToggleColumnVisibility('patientTransfer')}>
            <Checkbox size="small" checked={columnVisibility.patientTransfer} sx={{ mr: 1 }} />
            {t('isolation-requests.isolation-selection.columns.patient-transfer')}
          </MenuItem>
        </Menu>

        {/* Satır aralığı menüsü */}
        <Menu anchorEl={densityMenuAnchorEl} open={Boolean(densityMenuAnchorEl)} onClose={() => setDensityMenuAnchorEl(null)}>
          <MenuItem selected={rowDensity === 'compact'} onClick={() => handleChangeDensity('compact')}>
            Sıkı
          </MenuItem>
          <MenuItem selected={rowDensity === 'standard'} onClick={() => handleChangeDensity('standard')}>
            Normal
          </MenuItem>
          <MenuItem selected={rowDensity === 'comfortable'} onClick={() => handleChangeDensity('comfortable')}>
            Geniş
          </MenuItem>
        </Menu>

        {/* Üstte izolasyon seçimi tablosu */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            background: '#fff',
            maxHeight: isModalSelectionsExpanded ? 260 : 520,
            flexShrink: 0,
          }}
        >
          {isIsolationTypeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <LoadingScreen />
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead
                sx={{
                  '& th': {
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 700,
                    fontSize: '12px',
                    lineHeight: '24px',
                    letterSpacing: '0.17px',
                    backgroundColor: '#F4F7FE',
                    color: '#000000',
                    border: '1px solid #E0E0E0',
                    textAlign: 'center',
                  },
                }}
              >
                <TableRow>
                  <TableCell padding="checkbox" sx={{ py: 1, px: 1, textAlign: 'center' }}>
                    <Checkbox
                      color="primary"
                      indeterminate={false}
                      checked={allSelectableChecked}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setPendingIsolations((prev) => {
                            const next = [...prev];
                            selectable.forEach((option) => {
                              if (!next.some((p) => p.ist_id === option.id)) {
                                const tempId = `temp-${option.id}-${Date.now()}`;
                                next.push({
                                  id: tempId,
                                  ist_id: option.id,
                                  isolationtypename: option.name,
                                  icon: option.pics_svg,
                                  approve_status: '0',
                                  isolation_taken_reason: null,
                                  isolation_taken_descrption: '',
                                  isolation_without_bacteria: null,
                                  isolation_without_bact_descrp: '',
                                  is_given_family_education: '0',
                                  notification_lab: '0',
                                  notification_kitchen: '0',
                                  notification_cleaner: '0',
                                  start_date: null,
                                  finish_date: null,
                                  app_or_reject_date: null,
                                  isolation_taken_reason_label: '',
                                  isolation_without_bacteria_label: '',
                                });
                              }
                            });
                            return next;
                          });
                        } else {
                          setPendingIsolations((prev) =>
                            prev.filter((p) => !selectable.some((s) => s.id === p.ist_id))
                          );
                        }
                      }}
                    />
                  </TableCell>
                  {columnVisibility.icon && (
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {t('isolation-requests.isolation-selection.columns.icon')}
                    </TableCell>
                  )}
                  {columnVisibility.name && (
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {t('isolation-requests.isolation-selection.columns.isolation-name')}
                    </TableCell>
                  )}
                  {columnVisibility.description && (
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {t('isolation-requests.isolation-selection.columns.description')}
                    </TableCell>
                  )}
                  {columnVisibility.handHygiene && (
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {t('isolation-requests.isolation-selection.columns.hand-hygiene')}
                    </TableCell>
                  )}
                  {columnVisibility.gloves && (
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {t('isolation-requests.isolation-selection.columns.gloves')}
                    </TableCell>
                  )}
                  {columnVisibility.gown && (
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {t('isolation-requests.isolation-selection.columns.gown')}
                    </TableCell>
                  )}
                  {columnVisibility.mask && (
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {t('isolation-requests.isolation-selection.columns.mask')}
                    </TableCell>
                  )}
                  {columnVisibility.eyeProtection && (
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {t('isolation-requests.isolation-selection.columns.eye-protection')}
                    </TableCell>
                  )}
                  {columnVisibility.patientTransfer && (
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {t('isolation-requests.isolation-selection.columns.patient-transfer')}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOptions.map((option) => (
                  <TableRow key={option.id} hover sx={{ cursor: 'pointer', height: rowHeight }}>
                    <TableCell padding="checkbox" sx={{ py: 0.5, px: 1, textAlign: 'center' }}>
                      <Checkbox
                        color="primary"
                        checked={
                          addedIsolations.some((item) => item.ist_id === option.id) ||
                          pendingIsolations.some((item) => item.ist_id === option.id)
                        }
                        disabled={addedIsolations.some((item) => item.ist_id === option.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setPendingIsolations((prev) => {
                              if (prev.some((p) => p.ist_id === option.id)) {
                                return prev;
                              }
                              const tempId = `temp-${option.id}-${Date.now()}`;
                              const newItem = {
                                id: tempId,
                                ist_id: option.id,
                                isolationtypename: option.name,
                                icon: option.pics_svg,
                                approve_status: '0',
                                isolation_taken_reason: null,
                                isolation_taken_descrption: '',
                                isolation_without_bacteria: null,
                                isolation_without_bact_descrp: '',
                                is_given_family_education: '0',
                                notification_lab: '0',
                                notification_kitchen: '0',
                                notification_cleaner: '0',
                                start_date: null,
                                finish_date: null,
                                app_or_reject_date: null,
                                isolation_taken_reason_label: '',
                                isolation_without_bacteria_label: '',
                              };
                              return [...prev, newItem];
                            });
                          } else {
                            setPendingIsolations((prev) =>
                              prev.filter((p) => p.ist_id !== option.id)
                            );
                          }
                        }}
                      />
                    </TableCell>
                    {columnVisibility.icon && (
                      <TableCell>
                        {option.pics_svg && (
                          <img
                            src={getImageSrc(option.pics_svg)}
                            alt={option.name}
                            style={{ width: 32, height: 32, display: 'block', margin: '0 auto' }}
                          />
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.name && (
                      <TableCell sx={{ py: 0.5, px: 1 }}>{option.name}</TableCell>
                    )}
                    {columnVisibility.description && (
                      <TableCell sx={{ py: 0.5, px: 1 }}>{option.descrption}</TableCell>
                    )}
                    {columnVisibility.handHygiene && (
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        {option.hand_hygiene ?? option.elHijyeni}
                      </TableCell>
                    )}
                    {columnVisibility.gloves && (
                      <TableCell sx={{ py: 0.5, px: 1 }}>{option.glove ?? option.eldiven}</TableCell>
                    )}
                    {columnVisibility.gown && (
                      <TableCell sx={{ py: 0.5, px: 1 }}>{option.bib ?? option.onluk}</TableCell>
                    )}
                    {columnVisibility.mask && (
                      <TableCell sx={{ py: 0.5, px: 1 }}>{option.mask ?? option.maske}</TableCell>
                    )}
                    {columnVisibility.eyeProtection && (
                      <TableCell sx={{ py: 0.5, px: 1 }}>{option.glasses ?? option.gozluk}</TableCell>
                    )}
                    {columnVisibility.patientTransfer && (
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        {option.transfer_of_patient ?? option.hastaninTransferi}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pending selections table in panel */}
        <Box sx={{ mt: 3, mb: 1.25, mx: 1.25, flexShrink: 0 }}>
          <SelectedServicesPanel
            data={[]}
            selectedItems={[]}
            config={{
              title: t('isolation-requests.your-selections'),
              showVisibilityButton: true,
              showFullScreenButton: false,
              customButtons: [
                {
                  onClick: handleCommitPendingIsolations,
                  renderButton: () => (
                    <ByzMuiAddButton
                      onClick={handleCommitPendingIsolations}
                      sx={{
                        height: 30,
                        borderRadius: '4px',
                        padding: '4px 10px',
                        minWidth: 'unset',
                      }}
                    />
                  ),
                },
              ],
            }}
            columns={[]}
            gridHeight="320px"
            onItemToggle={() => { }}
            onSelectAll={() => { }}
            disableSelection
            position="relative"
            shadow="none"
            backgroundColor="#ffffff"
            headerBackgroundColor="#ffffff"
            defaultExpanded
            renderContent={(state: { isExpanded: boolean; isFullScreen: boolean }) => {
              if (!state.isExpanded) return null;
              const maxHeight = state.isFullScreen ? 'calc(80vh - 220px)' : '260px';
              return renderSelectionsTable(pendingIsolations, maxHeight);
            }}
            onExpandedChange={setIsModalSelectionsExpanded}
            onVisibilityClick={() => setIsEyeOpen((prev) => !prev)}
          />
        </Box>

        {isSavingPendingIsolations && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.6)',
              zIndex: 10,
            }}
          >
            <LoadingScreen />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('buttons.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IsolationSelectionModal;

